/**
 * LeftNavRail — collapsible left navigation (Linear / Notion pattern).
 *
 *   * 60px collapsed; expands to 240px on hover (overlay, no layout shift)
 *     or when pinned (page content shifts via the `--rail-width` variable).
 *   * Items come from `src/navigation.ts` (single config shared with the
 *     mobile NavDrawer and the command palette), filtered by role + feature flags.
 *   * Hosts the shell utilities: search (⌘K), notification bell, live-status.
 *   * Mobile (<768px): hidden; a floating hamburger opens NavDrawer.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { LogOut, UserCircle, Menu, PanelLeftClose, PanelLeftOpen, Search, Bell } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { ROLE_LABELS, ROLE_COLORS, roleMessageId } from '../utils/roles';
import { visibleNavItems, isNavItemActive, type NavItem } from '../navigation';
import { useFeatures } from '../hooks/useFeatures';
import { useLogout } from '../shell/useLogout';
import { LiveStatusPill } from '../shell/LiveStatusPill';
import { Kbd, MOD_KEY } from './ui/Kbd';
import type { ConnectionState } from '../services/websocket';
import NavDrawer from './NavDrawer';

const PIN_STORAGE_KEY = 'nav-rail-pinned';
const HOVER_OPEN_DELAY_MS = 200;
const HOVER_CLOSE_DELAY_MS = 100;

function readPinPreference(): boolean {
  try { return localStorage.getItem(PIN_STORAGE_KEY) === '1'; } catch { return false; }
}

export interface LeftNavRailProps {
  connectionState?: ConnectionState;
  onOpenPalette?: () => void;
}

export default function LeftNavRail({ connectionState = 'disconnected', onOpenPalette }: LeftNavRailProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, driverName } = useAuthStore();
  const { features } = useFeatures();
  const handleLogout = useLogout();
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const togglePanel = useNotificationStore(s => s.togglePanel);

  const [pinned, setPinned] = useState<boolean>(readPinPreference);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(PIN_STORAGE_KEY, pinned ? '1' : '0'); } catch { /* ignore */ }
    document.documentElement.style.setProperty('--rail-width', `${pinned ? 240 : 60}px`);
  }, [pinned]);

  useEffect(() => () => { if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current); }, []);

  const expanded = pinned || hoverExpanded;

  const scheduleHoverOpen = () => {
    if (pinned) return;
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => setHoverExpanded(true), HOVER_OPEN_DELAY_MS);
  };
  const scheduleHoverClose = () => {
    if (pinned) return;
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => setHoverExpanded(false), HOVER_CLOSE_DELAY_MS);
  };

  function go(path: string) {
    setMobileNavOpen(false);
    setHoverExpanded(false);
    navigate(path);
  }

  const items = visibleNavItems(role, features);
  const mainItems = items.filter(i => i.group === 'main');
  const platformItems = items.filter(i => i.group === 'platform');
  const legacyItems = items.filter(i => i.group === 'legacy');

  const initials = driverName
    ? driverName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const isQa = import.meta.env.VITE_ENVIRONMENT === 'qa';
  const impersonatedName = (typeof localStorage !== 'undefined') ? localStorage.getItem('fleet_impersonated_account_name') : null;

  const t = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        className="md:hidden fixed bottom-4 left-4 z-40 w-12 h-12 rounded-full bg-brand-700 text-white shadow-lg flex items-center justify-center hover:bg-brand-600 focus-ring"
        aria-label={t('shell.openMenu', 'Open navigation menu')}
      >
        <Menu className="w-5 h-5" />
        {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      <NavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} connectionState={connectionState} onOpenPalette={onOpenPalette} />

      <aside
        className="hidden md:flex fixed top-0 left-0 h-full z-30 flex-col bg-brand-900 text-white shadow-xl transition-[width] duration-200 ease-out"
        style={{ width: expanded ? 240 : 60 }}
        onMouseEnter={scheduleHoverOpen}
        onMouseLeave={scheduleHoverClose}
        aria-label={t('shell.primaryNav', 'Primary navigation')}
      >
        <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10 shrink-0">
          <img src="/logos/globe-white.png" alt="DeliveryOps" className="w-7 h-7 shrink-0" style={{ objectFit: 'contain' }} />
          {expanded && (
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="font-semibold text-sm truncate">DeliveryOps</h2>
              {isQa && <span className="bg-amber-500 text-amber-950 text-xs font-bold px-1.5 py-0.5 rounded">QA</span>}
            </div>
          )}
        </div>

        {expanded && impersonatedName && (
          <div className="px-3 py-2 bg-amber-500/15 border-b border-amber-300/20 shrink-0">
            <p className="text-xs font-semibold text-amber-200 uppercase tracking-wider">{t('shell.viewingAs', 'Viewing as')}</p>
            <p className="text-xs font-medium text-amber-100 truncate">{impersonatedName}</p>
          </div>
        )}

        {/* Utilities: search + notifications */}
        <div className="px-2 pt-2 shrink-0 space-y-0.5">
          <button
            type="button"
            onClick={() => { setHoverExpanded(false); onOpenPalette?.(); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-indigo-100 hover:bg-white/10 transition-colors text-sm focus-ring"
            title={expanded ? undefined : t('shell.search', 'Search')}
            aria-label={t('shell.searchAria', 'Search or jump to (Cmd/Ctrl+K)')}
          >
            <Search className="w-5 h-5 shrink-0" />
            {expanded && <span className="truncate flex-1 text-left">{t('shell.search', 'Search')}</span>}
            {expanded && <span className="flex gap-0.5 opacity-70"><Kbd className="bg-white/10 border-white/20 text-indigo-100 shadow-none">{MOD_KEY}</Kbd><Kbd className="bg-white/10 border-white/20 text-indigo-100 shadow-none">K</Kbd></span>}
          </button>
          <button
            type="button"
            onClick={() => { setHoverExpanded(false); togglePanel(); }}
            className="relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-indigo-100 hover:bg-white/10 transition-colors text-sm focus-ring"
            title={expanded ? undefined : t('shell.notifications', 'Notifications')}
            aria-label={unreadCount > 0
              ? intl.formatMessage({ id: 'shell.notificationsUnread', defaultMessage: 'Notifications, {count} unread' }, { count: unreadCount })
              : t('shell.notifications', 'Notifications')}
          >
            <span className="relative shrink-0">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </span>
            {expanded && <span className="truncate flex-1 text-left">{t('shell.notifications', 'Notifications')}</span>}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {platformItems.length > 0 && (
            <>
              {expanded && <p className="px-2 mb-1 text-xs font-semibold text-amber-200/80 uppercase tracking-wider">{t('shell.group.platform', 'Platform')}</p>}
              <ul className="space-y-0.5 mb-3">
                {platformItems.map(item => (
                  <RailItem key={item.id} item={item} label={t(item.labelId, item.label)} expanded={expanded} active={isNavItemActive(item, location.pathname)} onSelect={go} accent="amber" />
                ))}
              </ul>
            </>
          )}
          {expanded && <p className="px-2 mb-1 text-xs font-semibold text-indigo-300 uppercase tracking-wider">{t('shell.group.workspace', 'Workspace')}</p>}
          <ul className="space-y-0.5">
            {mainItems.map(item => (
              <RailItem key={item.id} item={item} label={t(item.labelId, item.label)} expanded={expanded} active={isNavItemActive(item, location.pathname)} onSelect={go} accent="indigo" />
            ))}
          </ul>
          {legacyItems.length > 0 && (
            <>
              {expanded && <p className="px-2 mt-3 mb-1 text-xs font-semibold text-indigo-300/70 uppercase tracking-wider">{t('shell.group.legacy', 'Legacy')}</p>}
              {!expanded && <div className="my-2 border-t border-white/10" />}
              <ul className="space-y-0.5">
                {legacyItems.map(item => (
                  <RailItem key={item.id} item={item} label={t(item.labelId, item.label)} expanded={expanded} active={isNavItemActive(item, location.pathname)} onSelect={go} accent="muted" />
                ))}
              </ul>
            </>
          )}
        </nav>

        <div className="px-2 pt-2 border-t border-white/10 shrink-0">
          <LiveStatusPill state={connectionState} expanded={expanded} />
          <button
            type="button"
            onClick={() => setPinned(!pinned)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-indigo-200 hover:bg-white/5 transition-colors focus-ring"
            title={pinned ? t('shell.collapseNav', 'Collapse nav') : t('shell.pinNav', 'Pin nav open')}
            aria-label={pinned ? t('shell.collapseNav', 'Collapse nav') : t('shell.pinNav', 'Pin nav open')}
            aria-pressed={pinned}
          >
            <span className="shrink-0">{pinned ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}</span>
            {expanded && <span className="text-sm font-medium truncate">{pinned ? t('shell.collapse', 'Collapse') : t('shell.pinOpen', 'Pin open')}</span>}
          </button>
        </div>

        <div className="px-2 pb-2 pt-1 border-t border-white/10 shrink-0">
          <button
            type="button"
            onClick={() => go('/profile')}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors text-left focus-ring"
            title={t('nav.profile', 'Profile')}
          >
            <div className="w-9 h-9 rounded-full bg-brand-700 text-white text-sm font-bold flex items-center justify-center shrink-0">{initials}</div>
            {expanded && (
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{driverName ?? 'User'}</p>
                {role && (
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded-full font-medium ${ROLE_COLORS[role] ?? 'bg-gray-200 text-gray-700'}`}>
                    {intl.formatMessage({ id: roleMessageId(role), defaultMessage: ROLE_LABELS[role] ?? role })}
                  </span>
                )}
              </div>
            )}
            {expanded && <UserCircle className="w-4 h-4 text-indigo-300 shrink-0" />}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-1 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-300 hover:bg-red-500/10 transition-colors focus-ring"
            title={t('auth.signOut', 'Sign out')}
            aria-label={t('auth.signOut', 'Sign out')}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {expanded && <span className="text-sm font-medium">{t('auth.signOut', 'Sign out')}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function RailItem({ item, label, expanded, active, onSelect, accent }: {
  item: NavItem; label: string; expanded: boolean; active: boolean; onSelect: (path: string) => void; accent: 'indigo' | 'amber' | 'muted';
}) {
  const activeBg = accent === 'amber' ? 'bg-amber-500/20 text-amber-100' : 'bg-white/15 text-white';
  const activeDot = accent === 'amber' ? 'bg-amber-300' : 'bg-indigo-200';
  const idle = accent === 'muted' ? 'text-indigo-300 hover:bg-white/5' : 'text-indigo-100 hover:bg-white/5';
  const Icon = item.icon;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(item.path)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-ring ${active ? activeBg : idle}`}
        title={expanded ? undefined : label}
        aria-label={expanded ? undefined : label}
        aria-current={active ? 'page' : undefined}
      >
        <span className="shrink-0"><Icon className="w-5 h-5" /></span>
        {expanded && <span className="truncate">{label}</span>}
        {expanded && active && <span className={`ml-auto w-1.5 h-1.5 rounded-full ${activeDot}`} />}
      </button>
    </li>
  );
}
